import type { WorkflowType } from '../components/WorkflowTypeGrid';

/** The durable MR route a viewer occupied when its rendered stage changed. */
export interface MarketResearchStageRoute {
  flowId: string | null;
  workflowType: WorkflowType | null;
  /** The active app surface distinguishes a rendered stage from its saved-flow list. */
  appPage: string;
  /** UI index distinguishes a user navigation from a branch-topology refresh. */
  stageIndex: number;
  stageCode: number | null;
}

/** A dotted stage whose authorized UI projection was successfully loaded. */
export interface LoadedMarketResearchStageEdit {
  flowId: string;
  stageCode: number;
  revision: number;
}

/**
 * An edit is read only when the viewer actually leaves the same, successfully
 * loaded MR stage. A transition to Manage Workflows, Home, or another flow is
 * still a genuine stage exit: the marker must not survive merely because the
 * old ``currentStage`` index remains in component state. Initial renders,
 * failed projections, and topology-only refreshes never acknowledge.
 */
export function shouldAcknowledgeMarketResearchStageOnExit(
  departing: MarketResearchStageRoute | null,
  next: MarketResearchStageRoute,
  candidate: LoadedMarketResearchStageEdit | null,
): candidate is LoadedMarketResearchStageEdit {
  return Boolean(
    departing
      && candidate
      && departing.workflowType === 'market_research'
      && departing.appPage === 'workflow'
      && departing.flowId
      && departing.stageCode
      && candidate.flowId === departing.flowId
      && candidate.stageCode === departing.stageCode
      && (
        departing.appPage !== next.appPage
        || departing.workflowType !== next.workflowType
        || departing.flowId !== next.flowId
        || departing.stageIndex !== next.stageIndex
      ),
  );
}
